import React, { useState } from 'react';
import { auth, googleProvider } from '../firebase';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import useDocumentTitle from '../lib/useDocumentTitle';
import { BookOpen, Mail, ArrowRight, ChevronDown } from 'lucide-react';

// Extract roles from claims (handles both legacy single-role and new array format)
function getRoles(claims) {
  if (Array.isArray(claims.roles)) return claims.roles;
  if (claims.role) return [claims.role];
  return [];
}

function redirectByRoles(roles) {
  if (roles.includes('superadmin')) {
    // The CRM moved off the root to /admin when the root became the toolkit
    // front door. Redirect on SIGN-IN only — / is never guarded, so superadmin
    // can still use the hub like any other licence-holder.
    window.location.href = '/admin';
  } else if (roles.includes('author')) {
    window.location.href = '/author/dashboard';
  } else if (roles.includes('reader')) {
    window.location.href = '/reader';
  } else {
    // No role set — might be a legacy account or new signup
    window.location.href = '/author/dashboard';
  }
}

export default function AuthorLogin() {
  useDocumentTitle('Sign in');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Password sign-in is collapsed by default. Buying a plan mints a LICENCE,
  // not an account, so plan-buyers have never set a password — for most people
  // arriving from narratoria.com the password form is a dead end, and it used
  // to be the visually primary action.
  const [showPassword, setShowPassword] = useState(false);

  async function handleEmailLogin() {
    setError('');
    setIsSubmitting(true);
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      const tokenResult = await user.getIdTokenResult();
      const roles = getRoles(tokenResult.claims);
      redirectByRoles(roles);
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
      } else {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleGoogleLogin() {
    setError('');
    setIsSubmitting(true);
    try {
      const { user } = await signInWithPopup(auth, googleProvider);
      const tokenResult = await user.getIdTokenResult();
      const roles = getRoles(tokenResult.claims);
      redirectByRoles(roles);
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100/30 p-4">
      <Card className="w-full max-w-md border-slate-200 shadow-lg">
        <CardContent className="p-8">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-100">
              <BookOpen className="h-7 w-7 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Sign in to Narratoria</h1>
            <p className="mt-1 text-sm text-slate-500">
              Your Publishing Checklist, Marketing Compass and Story Bible — all in one place.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* PRIMARY: Google. Licences resolve by verified email, so this is the
              route that works for everyone — members and plan-buyers alike. */}
          <Button
            className="w-full gap-2 bg-teal-600 text-white hover:bg-teal-700"
            onClick={handleGoogleLogin}
            disabled={isSubmitting}
          >
            <Mail className="h-4 w-4" />
            Continue with Google
          </Button>

          {/* SECONDARY: password, collapsed. */}
          {!showPassword ? (
            <button
              type="button"
              onClick={() => setShowPassword(true)}
              className="mx-auto mt-3 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              Sign in with a password instead <ChevronDown className="h-3 w-3" />
            </button>
          ) : (
            <div className="mt-4 space-y-4 rounded-xl border border-slate-200 p-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Your password"
                  onKeyDown={e => e.key === 'Enter' && handleEmailLogin()}
                />
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleEmailLogin}
                disabled={isSubmitting || !email || !password}
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </Button>
              <p className="text-center text-xs text-slate-400">
                Only for accounts created with a password. If you bought a plan, use Google above.
              </p>
            </div>
          )}

          {/* New visitors route to PLANS, not to a login form they can't use. */}
          <div className="mt-6 rounded-xl border border-teal-200 bg-teal-50/60 p-4 text-center">
            <p className="text-sm font-semibold text-teal-900">New here?</p>
            <p className="mt-1 text-xs leading-relaxed text-teal-800">
              See what's included and start a free 10-day trial — no card required.
            </p>
            <Button
              variant="outline"
              className="mt-3 gap-1.5 border-teal-300 bg-white text-teal-700 hover:bg-teal-100"
              onClick={() => { window.location.href = '/publishing-roadmap?view=plans'; }}
            >
              View plans <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>

          <p className="mt-6 text-center text-sm text-slate-500">
            Setting up an author account?{' '}
            <a href="/author/signup" className="font-medium text-teal-600 hover:text-teal-700">
              Create one
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
